import type { Contacto, Reserva } from "@/types";

/** Claves de localStorage usadas por la app (prefijo yg_ = Yexzabel Grace). */
export const STORAGE_KEYS = {
  reservas: "yg_reservas",
  contactos: "yg_contactos",
} as const;

/** Contrato mínimo de persistencia: permite cambiar a D1/KV sin tocar la lógica. */
export interface StorageAdapter {
  read(key: string): string | null;
  write(key: string, value: string): void;
}

/** Implementación sobre localStorage (navegador). */
export class LocalStorageAdapter implements StorageAdapter {
  read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  write(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* modo privado / cuota llena: la app sigue funcionando en memoria */
    }
  }
}

/** Implementación en memoria (tests y entornos sin localStorage). */
export class MemoryAdapter implements StorageAdapter {
  private readonly data = new Map<string, string>();

  read(key: string): string | null {
    return this.data.get(key) ?? null;
  }

  write(key: string, value: string): void {
    this.data.set(key, value);
  }
}

function parseList<T>(raw: string | null, fallback: T[]): T[] {
  if (!raw) return fallback;
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : fallback;
  } catch {
    return fallback;
  }
}

/** Fachada de datos de la app: reservas + contactos. */
export class Store {
  constructor(private readonly adapter: StorageAdapter = new LocalStorageAdapter()) {}

  getReservas(): Reserva[] {
    return parseList<Reserva>(this.adapter.read(STORAGE_KEYS.reservas), []);
  }

  setReservas(list: Reserva[]): void {
    this.adapter.write(STORAGE_KEYS.reservas, JSON.stringify(list));
  }

  getContactos(): Contacto[] {
    return parseList<Contacto>(this.adapter.read(STORAGE_KEYS.contactos), []);
  }

  setContactos(list: Contacto[]): void {
    this.adapter.write(STORAGE_KEYS.contactos, JSON.stringify(list));
  }
}

/** Instancia única usada por la app. */
export const store = new Store();
